import React from "react";

// Minimal, dependency-free Markdown renderer for AI chat messages.
// Renders straight to React elements (no dangerouslySetInnerHTML), so there is
// no HTML-injection surface no matter what the model outputs. Only http(s)/mailto
// links are turned into <a> — anything else (e.g. javascript:) stays plain text.
const SAFE_URL_RE = /^(https?:|mailto:)/i;
const INLINE_RE = /`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;

function renderInline(text, keyPrefix) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  INLINE_RE.lastIndex = 0;
  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<React.Fragment key={`${keyPrefix}-${key++}`}>{text.slice(lastIndex, match.index)}</React.Fragment>);
    }
    if (match[1] !== undefined) {
      nodes.push(<code key={`${keyPrefix}-${key++}`}>{match[1]}</code>);
    } else if (match[2] !== undefined) {
      const href = match[3];
      nodes.push(
        SAFE_URL_RE.test(href)
          ? <a key={`${keyPrefix}-${key++}`} href={href} target="_blank" rel="noopener noreferrer">{match[2]}</a>
          : <React.Fragment key={`${keyPrefix}-${key++}`}>{match[2]}</React.Fragment>
      );
    } else if (match[4] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-${key++}`}>{match[4]}</strong>);
    } else if (match[5] !== undefined) {
      nodes.push(<em key={`${keyPrefix}-${key++}`}>{match[5]}</em>);
    }
    lastIndex = INLINE_RE.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(<React.Fragment key={`${keyPrefix}-${key++}`}>{text.slice(lastIndex)}</React.Fragment>);
  }
  return nodes;
}

function renderParagraphLines(content, keyPrefix) {
  const lines = content.split("\n");
  const out = [];
  lines.forEach((line, i) => {
    if (i > 0) out.push(<br key={`${keyPrefix}-br-${i}`} />);
    out.push(...renderInline(line, `${keyPrefix}-${i}`));
  });
  return out;
}

function parseBlocks(text) {
  const lines = String(text || "").split("\n");
  const blocks = [];
  let paraBuffer = [];
  let listBuffer = null; // { type: 'ul' | 'ol', items: [] }
  let i = 0;

  const flushPara = () => {
    if (paraBuffer.length) {
      blocks.push({ type: "p", content: paraBuffer.join("\n") });
      paraBuffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer) {
      blocks.push(listBuffer);
      listBuffer = null;
    }
  };

  while (i < lines.length) {
    const line = lines[i];

    if (/^```/.test(line.trim())) {
      flushPara();
      flushList();
      const codeLines = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) {
        codeLines.push(lines[i]);
        i++;
      }
      blocks.push({ type: "code", content: codeLines.join("\n") });
      i++;
      continue;
    }

    const headingMatch = /^(#{1,6})\s+(.*)$/.exec(line);
    if (headingMatch) {
      flushPara();
      flushList();
      blocks.push({ type: "h", level: headingMatch[1].length, content: headingMatch[2] });
      i++;
      continue;
    }

    const bulletMatch = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numberedMatch = /^\s*\d+\.\s+(.*)$/.exec(line);
    if (bulletMatch || numberedMatch) {
      flushPara();
      const type = bulletMatch ? "ul" : "ol";
      const content = bulletMatch ? bulletMatch[1] : numberedMatch[1];
      if (!listBuffer || listBuffer.type !== type) {
        flushList();
        listBuffer = { type, items: [] };
      }
      listBuffer.items.push(content);
      i++;
      continue;
    }

    if (line.trim() === "") {
      flushPara();
      flushList();
      i++;
      continue;
    }

    flushList();
    paraBuffer.push(line);
    i++;
  }
  flushPara();
  flushList();
  return blocks;
}

function MarkdownText({ text }) {
  const blocks = parseBlocks(text);

  return (
    <>
      {blocks.map((block, idx) => {
        const key = `b-${idx}`;
        const isLast = idx === blocks.length - 1;
        const marginBottom = isLast ? 0 : 6;

        if (block.type === "h") {
          const Tag = `h${Math.min(block.level + 3, 6)}`;
          return (
            <Tag key={key} style={{ margin: `0 0 ${marginBottom}px 0`, fontSize: block.level <= 2 ? "1.05em" : "1em", fontWeight: 600 }}>
              {renderInline(block.content, key)}
            </Tag>
          );
        }
        if (block.type === "ul" || block.type === "ol") {
          const ListTag = block.type;
          return (
            <ListTag key={key} style={{ margin: `0 0 ${marginBottom}px 0`, paddingLeft: 18 }}>
              {block.items.map((item, j) => <li key={j}>{renderInline(item, `${key}-${j}`)}</li>)}
            </ListTag>
          );
        }
        if (block.type === "code") {
          return (
            <pre
              key={key}
              style={{
                background: "#F4F6FD",
                border: "1px solid #E5E9F7",
                borderRadius: 8,
                padding: "8px 10px",
                overflowX: "auto",
                margin: `0 0 ${marginBottom}px 0`,
              }}
            >
              <code>{block.content}</code>
            </pre>
          );
        }
        return (
          <p key={key} style={{ margin: `0 0 ${marginBottom}px 0` }}>
            {renderParagraphLines(block.content, key)}
          </p>
        );
      })}
    </>
  );
}

export default MarkdownText;
